import { db } from './firebase';
import { 
  collection, 
  onSnapshot,
  Unsubscribe,
  DocumentData,
  QuerySnapshot,
  doc,
  getDoc
} from 'firebase/firestore';
import { DriverLocation } from '../types/location.types';

export class LocationService {
  private static COLLECTION_NAME = 'driver_locations';

  // Subscribe to all active driver locations in real-time
  static subscribeToDriverLocations(callback: (locations: Record<string, DriverLocation>) => void): Unsubscribe {
    const locationsCollection = collection(db, this.COLLECTION_NAME);
    
    return onSnapshot(locationsCollection, (snapshot: QuerySnapshot<DocumentData>) => {
      const locations: Record<string, DriverLocation> = {};
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Only include active drivers
        if (data.status === 'active') {
          locations[doc.id] = {
            driverId: doc.id,
            latitude: data.latitude,
            longitude: data.longitude,
            accuracy: data.accuracy || 0,
            speed: data.speed || 0,
            altitude: data.altitude || 0,
            heading: data.heading || 0,
            timestamp: data.timestamp?.toDate() || new Date(),
            lastUpdated: data.last_updated || new Date().toISOString(),
            appState: data.app_state || 'unknown',
            status: data.status || 'offline'
          };
        }
      });
      
      callback(locations);
    }, (error) => {
      console.error('Error subscribing to driver locations:', error);
      callback({});
    });
  }

  // Get a specific driver's current location
  static async getDriverLocation(driverId: string): Promise<DriverLocation | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, driverId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          driverId: docSnap.id,
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy || 0,
          speed: data.speed || 0,
          altitude: data.altitude || 0,
          heading: data.heading || 0,
          timestamp: data.timestamp?.toDate() || new Date(),
          lastUpdated: data.last_updated || new Date().toISOString(),
          appState: data.app_state || 'unknown',
          status: data.status || 'offline'
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching driver location:', error);
      return null;
    }
  }

  // Subscribe to a specific driver's location
  static subscribeToDriverLocation(driverId: string, callback: (location: DriverLocation | null) => void): Unsubscribe {
    const docRef = doc(db, this.COLLECTION_NAME, driverId);
    
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const location: DriverLocation = {
          driverId: doc.id,
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy || 0,
          speed: data.speed || 0,
          altitude: data.altitude || 0,
          heading: data.heading || 0,
          timestamp: data.timestamp?.toDate() || new Date(),
          lastUpdated: data.last_updated || new Date().toISOString(),
          appState: data.app_state || 'unknown',
          status: data.status || 'offline'
        };
        callback(location);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error subscribing to driver location:', error);
      callback(null);
    });
  }
}